/**
 * ProblemVisual — renders the per-problem visual aid the model attaches via
 * `visualData`, at ALL grade levels. Arithmetic/early kinds: counting, array,
 * fraction, shape, fraction-sentence. Geometry kinds: coordinate-plane, polygon,
 * transversal. Returns null for anything it doesn't recognize, so it's safe on
 * every item.
 */

// A small shaded fraction bar (numerator of denominator parts filled), sized to
// sit inline within a fraction-sentence graphic.
function MiniFractionBar({ numerator, denominator }) {
  const n = Math.max(1, Math.min(Number(denominator) || 1, 12));
  const filled = Math.max(0, Math.min(Number(numerator) || 0, n));
  return (
    <div className="w-24 flex border-2 border-[#0A0A0A] rounded overflow-hidden">
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className={`flex-1 h-6 ${i < filled ? "bg-brand-coral" : "bg-white"} ${i < n - 1 ? "border-r-2 border-[#0A0A0A]" : ""}`}
        />
      ))}
    </div>
  );
}

// Graphic representation of a fraction word problem's givens: each given fraction
// drawn as a shaded bar (with its label), separated by the operator and ending in
// "= ?". Shows how the givens look, without stating the answer.
function FractionSentence({ fractions = [], operator = "+" }) {
  const fr = (Array.isArray(fractions) ? fractions : [])
    .map((f) => ({ n: Number(f?.numerator), d: Number(f?.denominator) }))
    .filter((f) => Number.isFinite(f.n) && Number.isFinite(f.d) && f.d > 0)
    .slice(0, 3);
  if (fr.length === 0) return null;
  const op = ["+", "-", "−", "×", "÷"].includes(String(operator)) ? String(operator) : "+";
  const nodes = [];
  fr.forEach((f, i) => {
    nodes.push(
      <div key={`f${i}`} className="flex flex-col items-center gap-1">
        <MiniFractionBar numerator={f.n} denominator={f.d} />
        <span className="text-sm font-bold text-[#0A0A0A]">{f.n}/{f.d}</span>
      </div>
    );
    if (i < fr.length - 1) {
      nodes.push(<span key={`o${i}`} className="text-2xl font-bold text-[#0A0A0A] px-0.5">{op}</span>);
    }
  });
  nodes.push(<span key="eq" className="text-2xl font-bold text-[#0A0A0A] px-0.5">= ?</span>);
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 py-2" data-testid="problem-visual-fraction-sentence">
      {nodes}
    </div>
  );
}

// Strip model (bar model) for whole-number word problems. Two modes:
//   combine   — the given parts as segments of one bar, "?" bracket for the total
//   take-away — the known total bracketed above; segments = known part + "?" part
// Segment widths are proportional to the numbers so the picture carries meaning.
function StripModel({ mode = "combine", parts = [], total, part }) {
  const COLORS = ["#FF8A80", "#80B6FF", "#FFD93D"];
  let segs, bracket;
  if (mode === "take-away") {
    const t = Number(total), p = Number(part);
    if (!Number.isFinite(t) || !Number.isFinite(p) || t <= 0 || p <= 0 || p >= t) return null;
    segs = [
      { label: String(p), value: p, color: COLORS[0] },
      { label: "?", value: t - p, color: "#F3F3EE" },
    ];
    bracket = { label: String(t), side: "top" };
  } else {
    const ps = (Array.isArray(parts) ? parts : [])
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0)
      .slice(0, 3);
    if (ps.length < 2) return null;
    segs = ps.map((v, i) => ({ label: String(v), value: v, color: COLORS[i % COLORS.length] }));
    bracket = { label: "?", side: "bottom" };
  }
  const sum = segs.reduce((s, x) => s + x.value, 0);
  const bar = (
    <div className="flex w-64 sm:w-80 h-10 border-2 border-[#0A0A0A] rounded overflow-hidden">
      {segs.map((s, i) => (
        <div
          key={i}
          className={`flex items-center justify-center font-bold text-sm text-[#0A0A0A] ${i > 0 ? "border-l-2 border-[#0A0A0A]" : ""}`}
          style={{ width: `${Math.max((s.value / sum) * 100, 14)}%`, background: s.color }}
        >
          {s.label}
        </div>
      ))}
    </div>
  );
  const brace = (
    <div className="flex flex-col items-center w-64 sm:w-80">
      <div className={`w-full border-x-2 border-[#0A0A0A] ${bracket.side === "top" ? "border-t-2 rounded-t" : "border-b-2 rounded-b"} h-2`} />
      <span className="text-sm font-bold text-[#0A0A0A]">{bracket.label}</span>
    </div>
  );
  return (
    <div className="flex flex-col items-center gap-1 py-2" data-testid="problem-visual-strip-model">
      {bracket.side === "top" ? (
        <>
          <span className="text-sm font-bold text-[#0A0A0A]">{bracket.label}</span>
          <div className="flex flex-col items-center w-64 sm:w-80">
            <div className="w-full border-x-2 border-t-2 border-[#0A0A0A] rounded-t h-2" />
          </div>
          {bar}
        </>
      ) : (
        <>
          {bar}
          {brace}
        </>
      )}
    </div>
  );
}

// Coerce a [{label,x,y}] list into finite-number points.
function _normPoints(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((p) => ({ label: p?.label, x: Number(p?.x), y: Number(p?.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}
function _range(r, d0, d1) {
  return Array.isArray(r) && r.length === 2 && Number(r[0]) < Number(r[1]) ? [Number(r[0]), Number(r[1])] : [d0, d1];
}

// Guard against an unusable counting icon — empty, a stray word, or a LaTeX
// command (all plain ASCII) — which renders as a missing-glyph "tofu" box and
// reads as a black blob. Fall back to a filled circle, which renders in every
// font. (A genuine emoji is non-ASCII and passes through unchanged.)
function _safeIcon(icon) {
  const s = String(icon ?? "").trim();
  if (!s || /\\/.test(s) || /^[\x00-\x7F]+$/.test(s)) return "●";
  return s;
}

function CountingRow({ count = 5, icon = "⭐", label }) {
  const items = Array.from({ length: Math.max(1, Math.min(Number(count) || 1, 12)) });
  const ic = _safeIcon(icon);
  return (
    <div className="flex flex-col items-center gap-2 py-2" data-testid="problem-visual-counting">
      <div className="text-3xl leading-none flex flex-wrap justify-center gap-1.5 max-w-md" aria-label={label || "counting aid"}>
        {items.map((_, i) => (
          <span key={i}>{ic}</span>
        ))}
      </div>
    </div>
  );
}

// Each addend of a number sentence gets its OWN boxed set, separated by the
// operator (+/−), so the picture mirrors the structure of the equation
// (Group 1 + Group 2 = Total) instead of one undifferentiated pile of objects.
function CountingGroups({ groups = [], operator = "+" }) {
  // The AI schema allows up to 12 groups (each 1-12 items) — matching that bound
  // here instead of an arbitrary lower cap prevents silently dropping groups from
  // problems like "6 groups of 4" (repeated addition / multiplication framing).
  const clean = (Array.isArray(groups) ? groups : [])
    .map((g) => ({ count: Math.max(1, Math.min(Number(g?.count) || 1, 12)), icon: _safeIcon(g?.icon) }))
    .slice(0, 12);
  if (clean.length === 0) return null;
  const op = ["+", "-", "−", "×", "÷"].includes(String(operator)) ? String(operator) : "+";
  const nodes = [];
  clean.forEach((g, gi) => {
    nodes.push(
      <div
        key={`g${gi}`}
        className="flex flex-wrap items-center justify-center gap-1 max-w-[7rem] rounded-lg border-2 border-[#0A0A0A]/20 bg-white px-2 py-1 text-2xl leading-none"
      >
        {Array.from({ length: g.count }).map((_, i) => (
          <span key={i}>{g.icon}</span>
        ))}
      </div>
    );
    if (gi < clean.length - 1) {
      nodes.push(
        <span key={`op${gi}`} className="text-xl font-bold text-[#0A0A0A] px-0.5">
          {op}
        </span>
      );
    }
  });
  // A smaller per-group box (down from text-3xl/10rem) lets more groups fit on
  // one row within the PDF export's real content width instead of wrapping after
  // just 3 — avoiding the wrap in the first place beats aligning it well.
  // justify-start (not center) keeps any row that still has to wrap left-aligned
  // under the row above, instead of centering independently and staggering
  // sideways. No max-width here — the row uses whatever width its container
  // actually has (live preview or PDF capture host), rather than an arbitrary cap.
  // py-1 (not py-2): the question text above and the answer box below already
  // carry their own gap from the parent's `grid gap-2`, so the full py-2 here
  // doubled up into a visibly large gap on both sides of the diagram — sat the
  // dots too low relative to the question and pushed the answer box too far
  // below the diagram.
  return (
    <div
      className="flex flex-wrap items-center justify-start gap-2 py-1"
      data-testid="problem-visual-counting-groups"
    >
      {nodes}
    </div>
  );
}

function FractionBar({ numerator = 1, denominator = 2 }) {
  const n = Math.max(1, Math.min(Number(denominator) || 1, 16));
  const filled = Math.max(0, Math.min(Number(numerator) || 0, n));
  return (
    <div className="py-2 max-w-sm" data-testid="problem-visual-fraction">
      <div className="flex border-[3px] border-[#0A0A0A] rounded-lg overflow-hidden">
        {Array.from({ length: n }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-9 ${i < filled ? "bg-brand-coral" : "bg-white"} ${i < n - 1 ? "border-r-[3px] border-[#0A0A0A]" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

function ArrayDots({ rows = 2, cols = 3 }) {
  const r = Math.max(1, Math.min(Number(rows) || 1, 10));
  const c = Math.max(1, Math.min(Number(cols) || 1, 10));
  return (
    <div className="py-2" data-testid="problem-visual-array">
      <div className="inline-grid gap-1.5" style={{ gridTemplateColumns: `repeat(${c}, 1fr)` }}>
        {Array.from({ length: r * c }).map((_, i) => (
          <div key={i} className="w-4 h-4 rounded-full bg-brand-blue border-2 border-[#0A0A0A]" />
        ))}
      </div>
    </div>
  );
}

function Shape({ shape = "rectangle" }) {
  if (shape === "triangle") {
    return (
      <svg viewBox="0 0 200 160" className="w-40" data-testid="problem-visual-shape">
        <polygon points="20,140 180,140 100,25" fill="#FFD93D" stroke="#0A0A0A" strokeWidth="4" strokeLinejoin="round" />
      </svg>
    );
  }
  if (shape === "circle") {
    return (
      <svg viewBox="0 0 160 160" className="w-32" data-testid="problem-visual-shape">
        <circle cx="80" cy="80" r="64" fill="#A8E6CF" stroke="#0A0A0A" strokeWidth="4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 200 130" className="w-40" data-testid="problem-visual-shape">
      <rect x="15" y="20" width="170" height="90" fill="#FFD93D" stroke="#0A0A0A" strokeWidth="4" />
    </svg>
  );
}

// Cartesian grid that plots labelled points and connects segments. With
// `vertices` (>=3) it draws a closed polygon instead.
function CoordinatePlane({ points = [], segments = [], vertices = null, xRange = [-5, 5], yRange = [-5, 5] }) {
  const isPolygon = Array.isArray(vertices) && _normPoints(vertices).length >= 3;
  const pts = isPolygon ? _normPoints(vertices) : _normPoints(points);
  const [xMin, xMax] = _range(xRange, -5, 5);
  const [yMin, yMax] = _range(yRange, -5, 5);
  const W = 200, H = 200, pad = 18;
  const sx = (x) => pad + ((x - xMin) / (xMax - xMin)) * (W - 2 * pad);
  const sy = (y) => H - pad - ((y - yMin) / (yMax - yMin)) * (H - 2 * pad);
  const gx = [], gy = [];
  for (let x = Math.ceil(xMin); x <= Math.floor(xMax); x++) gx.push(x);
  for (let y = Math.ceil(yMin); y <= Math.floor(yMax); y++) gy.push(y);
  const byLabel = {};
  pts.forEach((p) => { if (p.label != null) byLabel[String(p.label)] = p; });
  let edges = [];
  if (isPolygon) {
    edges = pts.map((p, i) => [p, pts[(i + 1) % pts.length]]);
  } else {
    edges = (Array.isArray(segments) ? segments : [])
      .map((s) => [byLabel[String(s?.from)], byLabel[String(s?.to)]])
      .filter(([a, b]) => a && b);
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-44" data-testid="problem-visual-coordinate">
      {gx.map((x) => <line key={`gx${x}`} x1={sx(x)} y1={pad} x2={sx(x)} y2={H - pad} stroke="#0A0A0A" strokeOpacity="0.12" strokeWidth="1" />)}
      {gy.map((y) => <line key={`gy${y}`} x1={pad} y1={sy(y)} x2={W - pad} y2={sy(y)} stroke="#0A0A0A" strokeOpacity="0.12" strokeWidth="1" />)}
      {yMin <= 0 && yMax >= 0 && <line x1={pad} y1={sy(0)} x2={W - pad} y2={sy(0)} stroke="#0A0A0A" strokeWidth="2" />}
      {xMin <= 0 && xMax >= 0 && <line x1={sx(0)} y1={pad} x2={sx(0)} y2={H - pad} stroke="#0A0A0A" strokeWidth="2" />}
      {edges.map(([a, b], i) => <line key={`e${i}`} x1={sx(a.x)} y1={sy(a.y)} x2={sx(b.x)} y2={sy(b.y)} stroke="#4D96FF" strokeWidth="3" strokeLinecap="round" />)}
      {pts.map((p, i) => (
        <g key={`p${i}`}>
          <circle cx={sx(p.x)} cy={sy(p.y)} r="4" fill="#FF6B6B" stroke="#0A0A0A" strokeWidth="1.5" />
          {p.label != null && <text x={sx(p.x) + 6} y={sy(p.y) - 6} fontSize="11" fontWeight="bold" fill="#0A0A0A">{String(p.label)}</text>}
        </g>
      ))}
    </svg>
  );
}

// Two parallel lines cut by a transversal, with optional angle labels placed in
// the four positions around each intersection (slot = "L1-TL" .. "L2-BR").
function Transversal({ angles = [] }) {
  const W = 220, H = 190;
  const base = { L1: [87, 60], L2: [133, 130] };
  const off = { TL: [-16, -4], TR: [9, -4], BL: [-16, 16], BR: [9, 16] };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-48" data-testid="problem-visual-transversal">
      <line x1="20" y1="60" x2="200" y2="60" stroke="#0A0A0A" strokeWidth="3" />
      <line x1="20" y1="130" x2="200" y2="130" stroke="#0A0A0A" strokeWidth="3" />
      {/* parallel marks */}
      <text x="150" y="55" fontSize="12" fontWeight="bold" fill="#0A0A0A">›</text>
      <text x="150" y="125" fontSize="12" fontWeight="bold" fill="#0A0A0A">›</text>
      <line x1="60" y1="20" x2="160" y2="170" stroke="#4D96FF" strokeWidth="3" />
      {(Array.isArray(angles) ? angles : []).map((a, i) => {
        const m = String(a?.slot || "").match(/^(L1|L2)-(TL|TR|BL|BR)$/);
        if (!m) return null;
        const [bx, by] = base[m[1]];
        const [dx, dy] = off[m[2]];
        return <text key={i} x={bx + dx} y={by + dy} fontSize="11" fontWeight="bold" fill="#FF6B6B">{String(a?.label ?? "")}</text>;
      })}
    </svg>
  );
}

export default function ProblemVisual({ data }) {
  if (!data || typeof data !== "object") return null;
  const kind = data.kind;
  let figure = null;
  if (kind === "counting-groups") figure = <CountingGroups groups={data.groups} operator={data.operator} />;
  else if (kind === "counting") {
    // A grouped payload may also arrive under the plain "counting" kind — route
    // it to the segregated renderer so addends stay visually separated.
    figure = Array.isArray(data.groups) && data.groups.length > 1
      ? <CountingGroups groups={data.groups} operator={data.operator} />
      : <CountingRow count={data.count} icon={data.icon} label={data.label} />;
  }
  else if (kind === "fraction") figure = <FractionBar numerator={data.numerator} denominator={data.denominator} />;
  else if (kind === "fraction-sentence") figure = <FractionSentence fractions={data.fractions} operator={data.operator} />;
  else if (kind === "strip-model") figure = <StripModel mode={data.mode} parts={data.parts} total={data.total} part={data.part} />;
  else if (kind === "array") figure = <ArrayDots rows={data.rows} cols={data.cols} />;
  else if (kind === "shape") figure = <Shape shape={data.shape} />;
  else if (kind === "coordinate-plane" || kind === "graph") figure = <CoordinatePlane points={data.points} segments={data.segments} xRange={data.xRange} yRange={data.yRange} />;
  else if (kind === "polygon") figure = <CoordinatePlane vertices={data.vertices} xRange={data.xRange} yRange={data.yRange} />;
  else if (kind === "transversal") figure = <Transversal angles={data.angles} />;
  if (!figure) return null;
  return (
    <div className="my-1 inline-flex rounded-xl border-2 border-[#0A0A0A]/15 bg-[#F9F9F6] px-3 py-1">
      {figure}
    </div>
  );
}
